import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ContactCard } from "@/components/crm/ContactCard";
import { ContactPicker } from "@/components/crm/ContactPicker";
import { AlertTriangle, ChevronDown, ChevronUp, Link2, Unlink, UserPlus, Loader2, IdCard, Info } from "lucide-react";
import { BASE_BY_TYPE, catOf } from "@/lib/crm";

function CandidateRow({ c, onLink, busy, testid }) {
  const extra = (c.categories || []).filter((k) => k !== BASE_BY_TYPE[c.type]);
  return (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
      <div className="min-w-0 text-sm">
        <span className="font-semibold text-foreground">{c.name}</span>
        <span className="ml-1.5 rounded-full bg-brema-card px-1.5 py-0.5 text-[10px] text-muted-foreground">{c.type_label}</span>
        {extra.map((k) => <span key={k} className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] ${catOf(k).cls}`}>{catOf(k).label}</span>)}
        {c.strength === "weak" && <span className="ml-1 rounded-full bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:text-amber-300">numero simile</span>}
        {c.phone && <span className="ml-1.5 text-xs text-muted-foreground">{c.phone}</span>}
      </div>
      <Button type="button" size="sm" className="h-7 rounded-full bg-primary text-[11px] text-primary-foreground" onClick={() => onLink(c)} disabled={busy} data-testid={testid}>Conferma</Button>
    </div>
  );
}

/** Pannello «Scheda contatto» nella conversazione WhatsApp: apri/modifica la scheda, associa o crea un contatto. */
export function ThreadContactPanel({ thread, msgId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [picker, setPicker] = useState(false);
  const [confirmUnlink, setConfirmUnlink] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const linked = !!thread?.contact_id;
  const stored = thread?.match_candidates || [];

  // Conversazione non associata e senza candidati memorizzati → cerca comunque possibili corrispondenze per numero
  useEffect(() => {
    if (linked || stored.length || !thread?.from_phone) { setSuggested([]); return; }
    api.get(`/contacts/search?q=${encodeURIComponent(thread.from_phone)}`)
      .then(({ data }) => setSuggested((data.results || []).filter((r) => r.strength === "strong" || r.strength === "weak")))
      .catch(() => setSuggested([]));
  }, [linked, stored.length, thread?.from_phone]);

  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); toast.success(okMsg); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Operazione non riuscita"); }
    finally { setBusy(false); }
  };
  const link = (r) => { setPicker(false); run(() => api.post(`/inbound/${msgId}/link-contact`, { contact_type: r.type, contact_id: r.id }), `Conversazione collegata a ${r.name}`); };
  const unlink = () => { setConfirmUnlink(false); run(() => api.post(`/inbound/${msgId}/unlink-contact`), "Contatto scollegato: messaggi e scheda restano intatti"); };
  const create = () => run(() => api.post(`/inbound/${msgId}/create-contact`, {}), "Scheda contatto creata e collegata");
  const candidates = stored.length ? stored : suggested;

  return (
    <div className="rounded-xl border border-border bg-brema-bg/40 p-3" data-testid="thread-contact-panel">
      {linked ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button type="button" onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground hover:text-brand" data-testid="thread-contact-toggle">
              <IdCard className="h-4 w-4 text-brand" /> Scheda contatto {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            <div className="flex flex-wrap gap-1.5">
              <Button type="button" size="sm" variant="outline" className="h-7 rounded-full text-[11px]" onClick={() => setPicker(true)} disabled={busy} data-testid="thread-contact-change-btn"><Link2 className="mr-1 h-3 w-3" /> Cambia contatto</Button>
              <Button type="button" size="sm" variant="ghost" className="h-7 rounded-full text-[11px] text-muted-foreground" onClick={() => setConfirmUnlink(true)} disabled={busy} data-testid="thread-contact-unlink-btn"><Unlink className="mr-1 h-3 w-3" /> Scollega</Button>
            </div>
          </div>
          {open && <div className="mt-3 border-t border-border pt-3"><ContactCard ctype={thread.contact_type} cid={thread.contact_id} compact onSaved={() => onChanged?.()} /></div>}
        </>
      ) : (
        <div className="space-y-2">
          <div className="flex items-start gap-2 text-sm text-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
            <div>
              <p className="font-medium" data-testid="thread-contact-unlinked">Conversazione non associata a un contatto</p>
              <p className="text-xs text-muted-foreground">
                {candidates.length ? "Il numero corrisponde a una o più anagrafiche: conferma tu quella corretta." : "Nessuna corrispondenza automatica: associa un contatto esistente oppure crea una scheda base."}
              </p>
            </div>
          </div>
          {thread?.manual_unlinked && (
            <p className="inline-flex items-center gap-1 rounded-md border border-border bg-brema-card px-2 py-1 text-[11px] text-muted-foreground" data-testid="thread-contact-manual-unlinked">
              <Info className="h-3 w-3" /> Scollegata manualmente: il riconoscimento automatico è sospeso finché non la ricolleghi tu.
            </p>
          )}
          {candidates.length > 0 && (
            <div className="space-y-1.5" data-testid="thread-contact-candidates">
              {candidates.map((c) => <CandidateRow key={`${c.type}-${c.id}`} c={c} onLink={link} busy={busy} testid={`thread-candidate-link-${c.type}-${c.id}`} />)}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5">
            <Button type="button" size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={() => setPicker(true)} disabled={busy} data-testid="thread-contact-link-btn"><Link2 className="mr-1 h-3.5 w-3.5" /> Associa a contatto esistente</Button>
            <Button type="button" size="sm" variant="outline" className="h-8 rounded-full text-xs" onClick={create} disabled={busy} data-testid="thread-contact-create-btn">
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <UserPlus className="mr-1 h-3.5 w-3.5" />} Crea scheda base
            </Button>
          </div>
        </div>
      )}
      <ContactPicker open={picker} onOpenChange={setPicker} onPick={link} initialQuery={thread?.from_phone || ""} />
      <AlertDialog open={confirmUnlink} onOpenChange={setConfirmUnlink}>
        <AlertDialogContent data-testid="thread-unlink-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Scollegare il contatto?</AlertDialogTitle>
            <AlertDialogDescription>I messaggi e la scheda restano intatti. Il numero aggiunto all'anagrafica dal collegamento viene rimosso e il riconoscimento automatico per questa conversazione resta sospeso finché non la ricolleghi.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="thread-unlink-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={unlink} data-testid="thread-unlink-confirm-btn">Scollega</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
